import { onDisconnect } from '@reatom/framework'

import type { Atom, Ctx } from '@reatom/framework'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFactory = (args: any) => Atom<any>
type ReatomFactoryCacheOptions<F extends AnyFactory> = {
  factory: F
  keyFn: (item: Parameters<F>[0]) => string
  /**
   * Remove cache when atom disconnects
   * @default true
   */
  clearOnDisconnect?: boolean
  dispose?: (ctx: Ctx, atom: ReturnType<F>) => void
}

export const reatomFactoryCache = <F extends AnyFactory>(
  options: ReatomFactoryCacheOptions<F>,
): F => {
  const { clearOnDisconnect = true, factory, keyFn } = options
  const cache = new Map<string, Atom<unknown>>()

  const wrapped = (params: Parameters<F>[0]): ReturnType<F> => {
    const key = keyFn(params)
    const existingAtom = cache.get(key)
    if (existingAtom) {
      return existingAtom as ReturnType<F>
    }
    const model = factory(params) as ReturnType<F>
    cache.set(key, model)

    onDisconnect(model, (ctx) => {
      if (clearOnDisconnect) {
        options.dispose?.(ctx, model)
        cache.delete(key)
      }
    })
    return model
  }

  return wrapped as unknown as F
}
